<template>
    <div
        at-o-card-form
        class="o-card-form card-form"
    >
        <ValidationObserver
            tag="form"
            ref="cardForm"
            autocomplete="off"
            @submit.enter.prevent="submitForm"
        >
            <div class="card-form__item">
                <MInputWrapper
                    :label-text="locale('card_number')"
                    :validation-rules="validationRules.card"
                >
                    <AInputText
                        v-model.trim="formData.card"
                        :mask="generateCardNumberMask"
                        :is-disabled="isDisabled"
                        inputmode="decimal"
                    />
                </MInputWrapper>
            </div>
            <div
                v-if="isCardHolderNameRequired"
                class="card-form__item"
            >
                <MInputWrapper
                    :label-text="locale('card_holder')"
                    :validation-rules="validationRules.cardHolder"
                >
                    <AInputText
                        v-model.trim="formData.cardHolder"
                        :allowed-characters="allowedCharacters.cardHolder"
                        :is-disabled="isDisabled"
                    />
                </MInputWrapper>
            </div>
            <div class="card-form__item">
                <div class="card-form__row">
                    <div class="card-form__col">
                        <div class="card-form__group">
                            <MInputWrapper
                                :label-text="locale('month')"
                                :validation-rules="validationRules.month"
                                validation-mode="aggressive"
                                slot-type="select"
                            >
                                <AInputSelect
                                    v-model="formData.month"
                                    :options-list="monthsList"
                                    :is-disabled="isDisabled"
                                    data-vv-validate-on="blur"
                                />
                            </MInputWrapper>

                            <MInputWrapper
                                :label-text="locale('year')"
                                :validation-rules="validationRules.year"
                                validation-mode="aggressive"
                                slot-type="select"
                            >
                                <AInputSelect
                                    v-model="formData.year"
                                    :is-disabled="isDisabled"
                                    :options-list="yearsList"
                                />
                            </MInputWrapper>
                        </div>
                    </div>
                    <div class="card-form__col card-form__col--cvv">
                        <MInputWrapper
                            :label-text="locale('cvv')"
                            :validation-rules="validationRules.cvv"
                        >
                            <AInputCvv
                                v-model.trim="formData.cvv"
                                :mask="cvvCardMask"
                                :is-disabled="isDisabled"
                            />
                        </MInputWrapper>
                    </div>
                </div>
            </div>
            <div class="card-form__button-wrapper">
                <button
                    :disabled="isDisabled"
                    type="submit"
                    class="card-form__button"
                >
                    {{ submitButtonText }}
                </button>
            </div>
        </ValidationObserver>
    </div>
</template>

<script>
import { ValidationObserver } from 'vee-validate';

import AInputCvv from '../../atoms/a-input-cvv';
import AInputText from '../../atoms/a-input-text';
import MInputWrapper from '../../molecules/m-input-wrapper';
import AInputSelect from '../../atoms/a-input-select/a-input-select';

import { locale } from '../../../utils/services/localization-service';
import { getCardMaskByCardType } from '../../../utils/services/card-service';
import {
    CVV_CARD_MASK,
    AMEX_CARD_MASK,
    OTHER_CARD_MASK,
    MAESTRO_CARD_MASK
} from '../../../utils/constants/card-masks';
import { getUrlParameterByName } from '../../../utils/services/url-service';
import {
    REG_EXP_CARD_CVV,
    REG_EXP_CARD_HOLDER,
    REG_EXP_CARD_NUMBER,
    REG_EXP_MAESTRO_CARD_NUMBER
} from '../../../utils/constants/regexp-constants';
import {
    PLATFORM_AC,
    THEME_URL_PARAMS
} from '../../../utils/constants/theme-config-constants';
import { CARD_TYPES } from '../../../utils/constants/card-types-constants';

const ALLOWED_CHARACTERS = {
    cardHolder: REG_EXP_CARD_HOLDER
};

export default {
    name: 'OCardForm',
    components: {
        AInputCvv,
        AInputText,
        AInputSelect,
        MInputWrapper,
        ValidationObserver
    },
    props: {
        /**
         * Set token
         */
        token: {
            type: String,
            default: ''
        },
        /**
         * Flag for disable sending form
         */
        isDisabled: {
            type: Boolean,
            default: false
        },
        /**
         * Flag for show and validate card holder name
         */
        isCardHolderNameRequired: {
            type: Boolean,
            default: true
        }
    },
    data() {
        return {
            sciPlatform: '',
            formData: {
                token: this.token,
                cardHolder: '',
                card: '',
                month: '',
                year: '',
                cvv: '',
            },
            minCardYear: new Date().getFullYear(),
            amexCardMask: AMEX_CARD_MASK,
            maestroCardMask: MAESTRO_CARD_MASK,
            otherCardMask: OTHER_CARD_MASK,
            cvvCardMask: CVV_CARD_MASK,
            allowedCharacters: ALLOWED_CHARACTERS,
        }
    },
    computed: {
        submitButtonText() {
            return this.sciPlatform === PLATFORM_AC ? locale('submit_ac') : locale('submit');
        },
        cardType() {
            let number = this.formData.card;
            let re = new RegExp('^4');
            if (number.match(re) != null) return CARD_TYPES.VISA;

            re = new RegExp('^(34|37)');
            if (number.match(re) != null) return CARD_TYPES.AMEX;

            re = new RegExp('^5[1-5]');
            if (number.match(re) != null) return CARD_TYPES.MASTERCARD;

            re = new RegExp('^6011');
            if (number.match(re) != null) return CARD_TYPES.DISCOVER

            re = new RegExp('^(5018|5020|5038|5893|6304|6759|6761|6762|6763)');
            if (number.match(re) != null) return CARD_TYPES.MAESTRO;

            re = new RegExp('^9792')
            if (number.match(re) != null) return CARD_TYPES.TROY;

            return '';
        },
        generateCardNumberMask() {
            return getCardMaskByCardType(this.cardType);
        },
        minCardMonth() {
            const minCardMonth = this.formData.year === this.minCardYear
                ? new Date().getMonth() + 1
                : 1;
            return minCardMonth
        },
        monthsList() {
            const months = 12;
            const createMonths = (item, index) => {
                const month = index + 1;
                return { value : month, text: `${this.formatMonth(month)}`, isDisabled: month < this.minCardMonth}
            };
            const monthsList = Array.from(new Array(months));
            return monthsList.map(createMonths);
        },
        yearsList() {
            let currentYear = new Date().getFullYear();
            const years = 20;
            const yearsList = () => {
                const year = currentYear++;
                return { value : year, text: year }
            };
            const monthsList = Array.from(new Array(years));
            return monthsList.map(yearsList);
        },
        validationRules() {
            const validationRules = {
                card: {
                    required: true,
                    credit_card: true,
                    regex: this.cardType === CARD_TYPES.MAESTRO ? REG_EXP_MAESTRO_CARD_NUMBER : REG_EXP_CARD_NUMBER
                },
                cardHolder: {
                    required: this.isCardHolderNameRequired,
                    regex: REG_EXP_CARD_HOLDER
                },
                month: {
                    required: true
                },
                year: {
                    required: true,
                    min_value: new Date().getFullYear()
                },
                cvv: {
                    required: true,
                    regex: REG_EXP_CARD_CVV
                }
            };
            if(this.formData.year && (this.formData.year === this.minCardYear)) {
                validationRules.month.min_value = this.minCardMonth
            }
            return validationRules;
        },
    },
    mounted() {
       this.getSciPlatform();
    },
    methods: {
        getSciPlatform() {
            this.sciPlatform = getUrlParameterByName(THEME_URL_PARAMS.SCI_PLATFORM);
        },
        formatMonth(month) {
            return month < 10 ? '0' + month : month;
        },
        async submitForm() {
            const isValid = await this.$refs.cardForm.validate();
            if (!isValid) return;

            const formData = {
                token: this.token,
                card: this.formData.card,
                month: this.formData.month,
                year: this.formData.year,
                cvv: this.formData.cvv
            };

            if (this.isCardHolderNameRequired) {
                formData.cardHolder = this.formData.cardHolder;
            }

            this.$emit('onSubmit', formData)
        },
        locale(key) {
            return locale(key);
        }
    }
};
</script>

<style lang="scss">
    @import 'theme';
</style>
